import type { FC } from "react";
import { Box, Container, Typography, Chip, Stack, Link } from "@mui/material";
import { motion } from "framer-motion";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import InstagramIcon from "@mui/icons-material/Instagram";
import TwitterIcon from "@mui/icons-material/Twitter";
import LinkedInIcon from "@mui/icons-material/LinkedIn";

const cards = [
  {
    icon: <PhoneOutlinedIcon sx={{ fontSize: 26 }} />,
    label: "Call Us",
    value: "+91 9560107106",
    sub: "Available 24 × 7",
    href: "tel:+919560107106",
    color: "#818CF8",
    glow: "rgba(99, 102, 241, 0.14)",
  },
  {
    icon: <EmailOutlinedIcon sx={{ fontSize: 26 }} />,
    label: "Email Us",
    value: "support@platinx.exchange",
    sub: "We reply within 2 hours",
    href: "mailto:support@platinx.exchange",
    color: "#22D3EE",
    glow: "rgba(34, 211, 238, 0.13)",
  },
  {
    icon: <LocationOnOutlinedIcon sx={{ fontSize: 26 }} />,
    label: "Visit Us",
    value: "Unit 1919, 19th Floor, Tower A,\nSpectrum Metro Mall,\nSector 75, Noida-201301, India",
    sub: "Mon – Sat, 9 AM – 7 PM IST",
    href: "https://www.google.com/maps/place/Spectrum+@+Metro,+Sector+75,+Noida,+Uttar+Pradesh+201301,+India",
    color: "#4ADE80",
    glow: "rgba(74, 222, 128, 0.12)",
  },
];

const socials = [
  { icon: <InstagramIcon sx={{ fontSize: 20 }} />, href: "https://www.instagram.com/platinx.exchange", color: "#E1306C", label: "Instagram" },
  { icon: <TwitterIcon sx={{ fontSize: 20 }} />, href: "https://x.com/PlatinXExchange", color: "#1DA1F2", label: "Twitter / X" },
  { icon: <LinkedInIcon sx={{ fontSize: 20 }} />, href: "https://www.linkedin.com/company/platinx-exchange", color: "#0A66C2", label: "LinkedIn" },
];

export const HomeContact: FC = () => (
  <Box
    component="section"
    id="contact"
    sx={{
      background: "linear-gradient(180deg, #08091A 0%, #06040F 100%)",
      py: { xs: 10, md: 15 },
      position: "relative",
      overflow: "hidden",
    }}
  >
    {/* Glow accents */}
    <Box sx={{
      position: "absolute", bottom: 0, left: 0, right: 0, height: 320,
      background: "linear-gradient(to top, rgba(99, 102, 241, 0.08), transparent)",
      zIndex: 0, pointerEvents: "none",
    }} />
    <Box sx={{
      position: "absolute", top: "20%", right: "-8%",
      width: 480, height: 480, borderRadius: "50%",
      background: "radial-gradient(circle, rgba(34, 211, 238, 0.12) 0%, transparent 70%)",
      filter: "blur(70px)", zIndex: 0, pointerEvents: "none",
    }} />
    <Box sx={{
      position: "absolute", top: "50%", left: "-5%",
      width: 360, height: 360, borderRadius: "50%",
      background: "radial-gradient(circle, rgba(109, 40, 217, 0.2) 0%, transparent 70%)",
      filter: "blur(80px)", zIndex: 0, pointerEvents: "none",
    }} />

    <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: { xs: 7, md: 9 } }}>
        <motion.div initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.55 }}>
          <Chip label="Get In Touch" sx={{
            mb: 2.5, bgcolor: "rgba(99, 102, 241, 0.18)", color: "#A5B4FC",
            border: "1px solid rgba(129, 140, 248, 0.38)", fontWeight: 700, letterSpacing: "0.1em",
          }} />
          <Typography variant="h2" sx={{ color: "#fff", fontWeight: 800, mb: 2, fontSize: { xs: "2rem", md: "3rem" }, lineHeight: 1.15 }}>
            Ready to{" "}
            <Box component="span" sx={{
              background: "linear-gradient(135deg, #818CF8, #22D3EE)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            }}>
              get started?
            </Box>
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.58)", fontSize: "1.05rem", maxWidth: 520, mx: "auto", lineHeight: 1.75 }}>
            Contact our team for remittance services, OTC trading queries, or partnership opportunities.
          </Typography>
        </motion.div>
      </Box>

      {/* Contact cards */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
        gap: 3, mb: { xs: 7, md: 10 },
      }}>
        {cards.map((c, i) => (
          <motion.div key={c.label} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}>
            <Link href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" sx={{ textDecoration: "none", display: "block", height: "100%" }}>
              <Box sx={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(129, 140, 248, 0.14)",
                borderRadius: 4, p: { xs: 3.5, md: 4 }, height: "100%",
                cursor: "pointer", transition: "all 0.35s ease",
                "&:hover": {
                  borderColor: c.color,
                  background: `${c.glow}`,
                  transform: "translateY(-6px)",
                  boxShadow: `0 20px 50px rgba(0,0,0,0.3), 0 0 30px ${c.glow}`,
                  "& .contact-icon": { background: `${c.color}28` },
                },
              }}>
                <Box className="contact-icon" sx={{
                  display: "inline-flex", p: 1.5, borderRadius: 2, mb: 3,
                  background: `${c.color}18`, color: c.color,
                  transition: "background 0.3s ease",
                }}>
                  {c.icon}
                </Box>
                <Typography sx={{ color: c.color, fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", mb: 1 }}>
                  {c.label}
                </Typography>
                <Typography sx={{ color: "#fff", fontWeight: 600, lineHeight: 1.65, fontSize: "0.93rem", mb: 1, whiteSpace: "pre-line" }}>
                  {c.value}
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.38)", fontSize: "0.78rem" }}>{c.sub}</Typography>
              </Box>
            </Link>
          </motion.div>
        ))}
      </Box>

      {/* Divider */}
      <Box sx={{ borderTop: "1px solid rgba(129, 140, 248, 0.14)", mb: { xs: 6, md: 8 } }} />

      {/* Socials */}
      <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.55, delay: 0.2 }}>
        <Box sx={{ textAlign: "center" }}>
          <Typography sx={{ color: "rgba(255,255,255,0.35)", mb: 3, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.14em", fontWeight: 700 }}>
            Follow Platinx Exchange
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center" mb={5}>
            {socials.map((s) => (
              <Link key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}>
                <Box sx={{
                  width: 46, height: 46, borderRadius: "50%",
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(129, 140, 248, 0.18)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "rgba(255,255,255,0.55)",
                  transition: "all 0.28s ease",
                  "&:hover": { background: `${s.color}1F`, borderColor: s.color, color: s.color, transform: "translateY(-3px)", boxShadow: `0 6px 18px ${s.color}33` },
                }}>
                  {s.icon}
                </Box>
              </Link>
            ))}
          </Stack>
          <Typography sx={{ color: "rgba(255,255,255,0.2)", fontSize: "0.82rem" }}>
            Platinx Exchange — Powering global remittance & OTC trading from Noida, India
          </Typography>
        </Box>
      </motion.div>
    </Container>
  </Box>
);
